/**
 * Renders a job_outputs image ref via the shared classifier (shared/outputUrlRef).
 * Same resolution path for library cards, chips, and main preview — no divergence.
 *
 * When `hubThumb` is set, applies the hub thumbnail loading treatment:
 * 150ms-delayed SquareLoader (glyph only — does not gate fetch/decode; delay
 * is presentational and outside LOADING_STANDARD), off-screen decode before
 * paint, then the already-decoded HTMLImageElement is mounted into the slot
 * (exactly one network response per thumb). 200ms opacity fade-in, terminal
 * failed state (ImageOff — surface-specific). Non-hub callers unchanged.
 *
 * Eager mode (prop or ArtworkEagerImagesContext): skip IntersectionObserver and
 * sign/load immediately. Exposes data-image-state=pending|done|failed for export wait.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type DragEvent,
} from "react";
import { ImageOff } from "lucide-react";
import {
  classifyOutputRef,
  resolveSignedOutputUrl,
} from "@/lib/signedOutputUrls";
import { cn } from "@/lib/utils";
import {
  SquareLoader,
  type SquareLoaderSize,
} from "@/components/ui/square-loader";
import { useArtworkEagerImages } from "@/features/footwear-studio/artwork/ArtworkEagerImagesContext";

type Props = {
  storageRef: string | null | undefined;
  alt: string;
  className?: string;
  /** Root margin for IntersectionObserver look-ahead (default 200px). */
  rootMargin?: string;
  /**
   * When true, skip IntersectionObserver and sign/load immediately.
   * When undefined, uses ArtworkEagerImagesContext (default false).
   */
  eager?: boolean;
  /**
   * Hub card thumbs: delayed animated SquareLoader + decode-before-show +
   * 200ms opacity fade. Leave unset for Review Console / chips / other callers.
   */
  hubThumb?: boolean;
  /**
   * With hubThumb: SquareLoader edge length. Default 24 (hub grid / angle rail).
   * Overlay hero (large empty area) passes 40 per LOADING_STANDARD §2.
   */
  hubThumbLoaderSize?: SquareLoaderSize;
  /**
   * With hubThumb: fade a white plate under the image (letterbox bars stay
   * white after load). Slot behind stays dark (caller `bg-bgNested`) so the
   * loader stroke reads while pending. Hub grid cards leave this unset.
   */
  hubThumbWhitePlate?: boolean;
  /**
   * With hubThumbWhitePlate: extend the fading white host below the image by
   * this many px so a sibling label strip can sit on the same plate (one fade,
   * HUB_FADE_MS). Root height must include the extension. Image + loader stay
   * confined to the top (height − extend) band.
   */
  hubThumbWhitePlateExtendBottomPx?: number;
  /** Fires when the hub fade opaque flag flips (same rAF as the plate/image fade). */
  onHubOpaqueChange?: (opaque: boolean) => void;
  /**
   * light: loader and failed glyph drawn in dark ink for light backgrounds
   * (LOADING_STANDARD §8). Default dark keeps §1 view-cube white stroke.
   */
  hubThumbSurface?: "dark" | "light";
  /**
   * When false, suppress the browser's native HTML image drag (draggable=false
   * + dragstart preventDefault). Default true leaves every existing caller
   * unchanged. Collection Plan passes false so PlanWall pointer drags are not
   * aborted by pointercancel from native DnD.
   */
  nativeDrag?: boolean;
};

type HubPhase = "idle" | "pending" | "ready" | "failed";
/** DOM attribute for PDF export wait — pending until load, then done / failed. */
export type SignedImageDomState = "pending" | "done" | "failed";

/** Presentational only — delays when the SquareLoader glyph mounts, not fetch/decode. Outside LOADING_STANDARD. */
const HUB_LOADER_DELAY_MS = 150;
/** LOADING_STANDARD §4 — fade-in after decode-before-show. */
export const HUB_FADE_MS = 200;

export function SignedOutputImage({
  storageRef,
  alt,
  className,
  rootMargin = "200px",
  eager: eagerProp,
  hubThumb = false,
  hubThumbLoaderSize = 24,
  hubThumbWhitePlate = false,
  hubThumbWhitePlateExtendBottomPx = 0,
  onHubOpaqueChange,
  hubThumbSurface = "dark",
  nativeDrag = true,
}: Props) {
  const eagerFromContext = useArtworkEagerImages();
  const eager = eagerProp ?? eagerFromContext;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const imageHostRef = useRef<HTMLDivElement | null>(null);
  const decodedImgRef = useRef<HTMLImageElement | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [hubPhase, setHubPhase] = useState<HubPhase>("idle");
  const [showHubLoader, setShowHubLoader] = useState(false);
  const [hubOpaque, setHubOpaque] = useState(false);
  const [imageState, setImageState] = useState<SignedImageDomState>("pending");
  const decodeGen = useRef(0);

  const kind = classifyOutputRef(storageRef).kind;

  useEffect(() => {
    setFailed(false);
    setSrc(null);
    setHubPhase("idle");
    setShowHubLoader(false);
    setHubOpaque(false);
    setImageState("pending");
    setVisible(false);
    decodeGen.current += 1;
    decodedImgRef.current = null;
  }, [storageRef, hubThumb]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    if (kind !== "sign" || eager) {
      setVisible(true);
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, kind, storageRef, eager]);

  // Resolve signed / passthrough URL once visible.
  useEffect(() => {
    if (!visible) return;
    const classified = classifyOutputRef(storageRef);
    if (classified.kind === "empty") {
      setSrc(null);
      setFailed(true);
      setImageState("failed");
      if (hubThumb) setHubPhase("failed");
      return;
    }
    if (classified.kind === "passthrough") {
      setSrc(classified.url);
      return;
    }
    let cancelled = false;
    if (hubThumb) setHubPhase("pending");
    void resolveSignedOutputUrl(storageRef).then((url) => {
      if (cancelled) return;
      if (!url) {
        setSrc(null);
        setFailed(true);
        setImageState("failed");
        if (hubThumb) setHubPhase("failed");
        return;
      }
      setSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [visible, storageRef, hubThumb]);

  // Hub: decode off-screen; keep that element and mount it (one network response).
  useEffect(() => {
    if (!hubThumb) return;
    if (!src) return;
    if (failed) {
      setHubPhase("failed");
      setImageState("failed");
      return;
    }
    const gen = ++decodeGen.current;
    setHubPhase("pending");
    setImageState("pending");
    setHubOpaque(false);
    decodedImgRef.current = null;
    let cancelled = false;
    const img = new Image();
    img.decoding = "async";
    img.src = src;
    const finishOk = () => {
      if (cancelled || decodeGen.current !== gen) return;
      decodedImgRef.current = img;
      setHubPhase("ready");
      setImageState("done");
      requestAnimationFrame(() => {
        if (cancelled || decodeGen.current !== gen) return;
        setHubOpaque(true);
      });
    };
    const finishFail = () => {
      if (cancelled || decodeGen.current !== gen) return;
      setFailed(true);
      setHubPhase("failed");
      setImageState("failed");
    };
    void (async () => {
      try {
        if (typeof img.decode === "function") {
          await img.decode();
        } else if (!img.complete) {
          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error("load failed"));
          });
        }
        finishOk();
      } catch {
        finishFail();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hubThumb, src, failed]);

  // Mount the decoded HTMLImageElement once; only update opacity afterward.
  useLayoutEffect(() => {
    if (!hubThumb) return;
    const host = imageHostRef.current;
    if (!host) return;

    if (hubPhase !== "ready") {
      while (host.firstChild) host.removeChild(host.firstChild);
      return;
    }

    const img = decodedImgRef.current;
    if (!img) return;

    if (img.parentNode !== host) {
      while (host.firstChild) host.removeChild(host.firstChild);
      img.alt = alt;
      img.setAttribute("data-testid", "hub-thumb-image");
      img.className = "h-full w-full object-contain";
      img.decoding = "sync";
      img.onerror = () => {
        setFailed(true);
        setHubPhase("failed");
        setImageState("failed");
      };
      host.appendChild(img);
    }

    // White plate: fade host (bg-white + image) together. Hub cards: fade img only.
    if (hubThumbWhitePlate) {
      host.className = "absolute inset-0 h-full w-full bg-white";
      host.style.transition = `opacity ${HUB_FADE_MS}ms ease`;
      host.style.opacity = hubOpaque ? "1" : "0";
      img.style.opacity = "1";
      img.style.transition = "";
      // Keep the shoe in the top band; plate still fills the extended label area.
      if (hubThumbWhitePlateExtendBottomPx > 0) {
        img.className = "absolute top-0 left-0 w-full object-contain";
        img.style.height = `calc(100% - ${hubThumbWhitePlateExtendBottomPx}px)`;
      } else {
        img.className = "h-full w-full object-contain";
        img.style.height = "";
        img.style.top = "";
        img.style.left = "";
      }
    } else {
      host.className = "h-full w-full";
      host.style.opacity = "";
      host.style.transition = "";
      img.className = "h-full w-full object-contain";
      img.style.height = "";
      img.style.top = "";
      img.style.left = "";
      img.style.transition = `opacity ${HUB_FADE_MS}ms ease`;
      img.style.opacity = hubOpaque ? "1" : "0";
    }

    if (nativeDrag === false) {
      img.setAttribute("draggable", "false");
      img.ondragstart = (ev) => ev.preventDefault();
    } else {
      img.removeAttribute("draggable");
      img.ondragstart = null;
    }
  }, [
    hubThumb,
    hubThumbWhitePlate,
    hubThumbWhitePlateExtendBottomPx,
    hubPhase,
    alt,
    hubOpaque,
    nativeDrag,
  ]);

  useEffect(() => {
    onHubOpaqueChange?.(hubOpaque);
  }, [hubOpaque, onHubOpaqueChange]);

  // Hub: mount loader glyph only if still pending after 150ms (presentational).
  useEffect(() => {
    if (!hubThumb) {
      setShowHubLoader(false);
      return;
    }
    if (hubPhase !== "pending") {
      setShowHubLoader(false);
      return;
    }
    const t = window.setTimeout(() => {
      setShowHubLoader(true);
    }, HUB_LOADER_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [hubThumb, hubPhase]);

  // Non-hub: visible + empty src after resolve attempt → failed (already set);
  // keep pending while signing (visible && src == null && !failed).
  useEffect(() => {
    if (hubThumb) return;
    if (failed) {
      setImageState("failed");
      return;
    }
    if (!visible) {
      setImageState("pending");
      return;
    }
    if (!src) {
      // still signing, or empty handled above
      setImageState(kind === "empty" ? "failed" : "pending");
    }
  }, [hubThumb, failed, visible, src, kind]);

  // Non-hub legacy path (immediate img once src is set).
  if (!hubThumb) {
    const showEmpty = kind === "empty" || failed || (visible && src == null);
    return (
      <div
        ref={rootRef}
        className={cn("relative", className)}
        data-image-state={imageState}
      >
        {!showEmpty && src ? (
          <img
            src={src}
            alt={alt}
            className="h-full w-full object-contain"
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            {...(nativeDrag === false
              ? {
                  draggable: false,
                  onDragStart: (e: DragEvent<HTMLImageElement>) =>
                    e.preventDefault(),
                }
              : {})}
            onLoad={() => {
              setImageState("done");
            }}
            onError={() => {
              setFailed(true);
              setSrc(null);
              setImageState("failed");
            }}
          />
        ) : (
          <div className="h-full w-full" aria-label={alt} role="img" />
        )}
      </div>
    );
  }

  // Hub thumb treatment
  const busy = hubPhase === "pending" || hubPhase === "idle";
  const showFailed = hubPhase === "failed" || (kind === "empty" && visible);
  const showImage = hubPhase === "ready";
  const extendBottom = hubThumbWhitePlateExtendBottomPx;
  const imageBandStyle =
    extendBottom > 0
      ? ({ top: 0, left: 0, right: 0, height: `calc(100% - ${extendBottom}px)` } as const)
      : undefined;
  const imageBandClass =
    extendBottom > 0
      ? "pointer-events-none absolute flex items-center justify-center"
      : "pointer-events-none absolute inset-0 flex items-center justify-center";
  const failedBandClass =
    extendBottom > 0
      ? "absolute flex items-center justify-center"
      : "absolute inset-0 flex items-center justify-center";

  return (
    <div
      ref={rootRef}
      className={cn("relative h-full w-full", className)}
      aria-busy={busy ? true : undefined}
      data-hub-thumb-phase={hubPhase}
      data-hub-opaque={hubOpaque ? "true" : "false"}
      data-image-state={imageState}
      data-testid="hub-thumb-slot"
      data-hub-thumb="true"
    >
      {showImage ? (
        <div
          ref={imageHostRef}
          className={cn(
            hubThumbWhitePlate ? "absolute inset-0 h-full w-full bg-white" : "h-full w-full",
          )}
          style={
            hubThumbWhitePlate
              ? { opacity: 0, transition: `opacity ${HUB_FADE_MS}ms ease` }
              : undefined
          }
          data-testid="hub-thumb-image-host"
        />
      ) : null}

      {showHubLoader && hubPhase === "pending" ? (
        <div
          className={cn(
            imageBandClass,
            hubThumbSurface === "light" && "text-black/50",
          )}
          style={imageBandStyle}
          data-testid="hub-thumb-loader"
        >
          {hubThumbSurface === "light" ? (
            <SquareLoader
              size={hubThumbLoaderSize}
              tone="inherit"
              aria-hidden
            />
          ) : (
            <SquareLoader size={hubThumbLoaderSize} aria-hidden />
          )}
        </div>
      ) : null}

      {showFailed ? (
        <div
          className={failedBandClass}
          style={imageBandStyle}
          data-testid="hub-thumb-failed"
          role="img"
          aria-label={`${alt} unavailable`}
        >
          <ImageOff
            className={
              hubThumbSurface === "light"
                ? "h-4 w-4 text-black/30"
                : "h-4 w-4 text-white/25"
            }
            strokeWidth={1.5}
            aria-hidden
          />
        </div>
      ) : null}

      {/* Reserve space / empty slot while idle/pending without loader yet */}
      {!showImage && !showFailed && !showHubLoader ? (
        <div className="h-full w-full" aria-hidden />
      ) : null}
    </div>
  );
}
